import { apiClient } from '../config'

import type { AdNamesLookupResponse, CreateDoctorRequest, LoginResponse, ResetPasswordRequest, UpdateUserRequest, User, UserAnalytics, UserRole, UserType, UsersExportResponse, UsersResponse } from '../types'

const EXPORT_PAGE_TIMEOUT_MS = 60_000

export const usersService = {
  getUsers: async (params: {
    role?: UserRole
    userType?: UserType
    search?: string
    page?: number
    size?: number
  }, options?: { signal?: AbortSignal }): Promise<UsersResponse> => {
    const response = await apiClient.get<UsersResponse>('/users', { params, ...(options?.signal ? { signal: options.signal } : {}) })
    return response.data
  },

  getAllUsers: async (role?: UserRole, userType?: UserType): Promise<User[]> => {
    const params: Record<string, string | number> = {}
    if (role !== undefined) {
      params.role = role
    }
    if (userType !== undefined) {
      params.userType = userType
    }
    const allUsers: User[] = []
    let page = 1
    const size = 200
    let hasMore = true
    while (hasMore) {
      const response = await apiClient.get<UsersResponse>('/users', {
        params: { ...params, page, size },
      })
      const newUsers = response.data.users
      if (newUsers.length === 0) break
      allUsers.push(...newUsers)
      hasMore = allUsers.length < response.data.total
      page++
    }
    return allUsers
  },

  getUserAnalytics: async (): Promise<UserAnalytics> => {
    const response = await apiClient.get<UserAnalytics>('/users/analytics')
    return response.data
  },

  getExportUsers: async (role?: UserRole, userType?: UserType): Promise<User[]> => {
    const params: Record<string, string | number> = {}
    if (role !== undefined) {
      params.role = role
    }
    if (userType !== undefined) {
      params.userType = userType
    }
    const allUsers: User[] = []
    let afterId: string | undefined
    do {
      const response = await apiClient.get<UsersExportResponse>('/users/export', {
        params: afterId === undefined ? params : { ...params, afterId },
        timeout: EXPORT_PAGE_TIMEOUT_MS,
      })
      allUsers.push(...response.data.items)
      afterId = response.data.nextCursor ?? undefined
    } while (afterId !== undefined)
    return allUsers
  },

  lookupAdNames: async (emails: string[]): Promise<AdNamesLookupResponse> => {
    const response = await apiClient.post<AdNamesLookupResponse>('/users/ad-names', { emails }, {
      timeout: EXPORT_PAGE_TIMEOUT_MS,
    })
    return response.data
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await apiClient.get<User>(`/users/${id}`)
    return response.data
  },

  createDoctor: async (doctorData: CreateDoctorRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/credentials/register/doctor', doctorData)
    return response.data
  },

  resetPassword: async (resetPasswordData: ResetPasswordRequest): Promise<void> => {
    await apiClient.post(`/users/${resetPasswordData.userId}/reset-password`, { newPassword: resetPasswordData.newPassword })
  },

  updateUser: async (userId: string, updates: UpdateUserRequest): Promise<User> => {
    const response = await apiClient.patch<User>(`/users/${userId}`, updates)
    return response.data
  },
}
